import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../../ui/UIElements/Icon";
import Pagination from "../../../ui/Data/Pagination";
import CampaignsLoading from "../../../ui/Campaigns/CampaignsLoading";
import CampaignsError from "../../../ui/Campaigns/CampaignsError";
import {
  useCartRecoveryRules,
  useUpdateCartRecoveryRule,
} from "../../../../hooks/useCartRecovery";
import { useUrlStates, useToast } from "@finitless/shared/hooks";
import { handleApiError } from "@finitless/shared/lib";
import { Switch } from "../../../ui/UIElements/Switch";
import {
  CAMPAIGNS_PER_PAGE,
  MAX_VISIBLE_PAGES,
} from "../../../../constants/campaignConstants";

const truncateText = (text, maxLen = 72) => {
  if (!text || typeof text !== "string") return "";
  const trimmed = text.replace(/\s+/g, " ").trim();
  if (trimmed.length <= maxLen) return trimmed;
  return `${trimmed.slice(0, maxLen)}…`;
};

const getPrimaryDelayMinutes = (rule) => {
  const times = rule?.times;
  if (!Array.isArray(times) || times.length === 0) return null;
  const first = times[0];
  return typeof first === "number" ? first : null;
};

const CartRecoveryPage = () => {
  const { t } = useTranslation("cartRecovery");
  const { t: tCommon } = useTranslation("common");
  const navigate = useNavigate();
  const toast = useToast();
  const updateRuleMutation = useUpdateCartRecoveryRule();

  const initialUrlStates = useMemo(
    () => ({
      searchTerm: "",
      currentPage: "1",
    }),
    [],
  );

  const [, updateUrlState] = useUrlStates(initialUrlStates);

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [togglingRuleId, setTogglingRuleId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      updateUrlState("searchTerm", searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, updateUrlState]);

  const { data, isLoading, error, refetch } = useCartRecoveryRules({
    page: currentPage,
    pageSize: CAMPAIGNS_PER_PAGE,
    searchTerm,
  });

  const rules = data?.rules || [];
  const pagination = data?.pagination || {};

  const calculatedTotalPages = pagination?.total
    ? Math.ceil(pagination.total / CAMPAIGNS_PER_PAGE)
    : 1;

  const totalPages = pagination?.total_pages || calculatedTotalPages;

  const handleSearchChange = (term) => {
    setSearchInput(term);
    setCurrentPage(1);
    updateUrlState("currentPage", "1");
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
    updateUrlState("searchTerm", "");
    updateUrlState("currentPage", "1");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateUrlState("currentPage", String(page));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleViewDetails = (rule) => {
    navigate(`/growth/cart-recovery/${rule.id}`, { state: { rule } });
  };

  const handleCreateRule = () => {
    navigate("/growth/cart-recovery/new");
  };

  const handleRuleEnabledChange = async (rule, enabled) => {
    if (rule.enabled === enabled) return;
    setTogglingRuleId(rule.id);
    try {
      await updateRuleMutation.mutateAsync({
        ruleId: rule.id,
        data: { enabled },
      });
    } catch (error) {
      handleApiError(error, toast, {
        title: tCommon("states.error"),
        defaultMessage: t("toggle.error"),
      });
    } finally {
      setTogglingRuleId(null);
    }
  };

  const formatDelay = (rule) => {
    const minutes = getPrimaryDelayMinutes(rule);
    if (minutes == null) return "—";
    return t("delay.minutesShort", { count: minutes });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <h1 className="text-2xl font-bold text-white">{t("header.title")}</h1>
          <p className="mt-1 text-gray-400">{t("header.subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCreateRule}
            className="flex items-center space-x-2 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white transition-colors hover:bg-blue-700 sm:px-4"
          >
            <Icon name="Plus" className="h-4 w-4" />
            <span>{t("actions.newRule")}</span>
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-800 bg-gray-900 p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="min-w-0 flex-1">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon name="Search" className="h-4 w-4 text-gray-500" />
              </div>
              <input
                type="search"
                autoComplete="off"
                placeholder={t("search.placeholder")}
                value={searchInput}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="h-10 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 pr-10 pl-10 text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                aria-label={t("search.placeholder")}
              />
              {searchInput ? (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute top-1/2 right-2 flex h-5 w-5 -translate-y-1/2 transform items-center justify-center rounded-full bg-gray-600 transition-colors hover:bg-gray-500"
                >
                  <Icon name="X" className="h-3 w-3 text-gray-300" />
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <CampaignsLoading />
      ) : error ? (
        <CampaignsError error={error} onRetry={refetch} />
      ) : rules.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-xl border border-gray-800 bg-gray-900">
            <table
              className="w-full min-w-[700px]"
              style={{ tableLayout: "fixed" }}
            >
              <colgroup>
                <col style={{ width: "32%" }} />
                <col style={{ width: "38%" }} />
                <col style={{ width: "15%" }} />
                <col style={{ width: "15%" }} />
              </colgroup>
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-4 py-4 text-left text-xs font-medium tracking-wider text-gray-400 uppercase sm:px-6">
                    {t("table.columns.name")}
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-medium tracking-wider text-gray-400 uppercase sm:px-6">
                    {t("table.columns.description")}
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-medium tracking-wider text-gray-400 uppercase sm:px-6">
                    {t("table.columns.delay")}
                  </th>
                  <th className="px-4 py-4 text-center text-xs font-medium tracking-wider text-gray-400 uppercase sm:px-6">
                    {t("table.columns.status")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800/50">
                {rules.map((rule) => {
                  const name =
                    rule.name?.trim() || t("table.fallback.untitled");
                  const description = truncateText(rule.description || "");

                  return (
                    <tr
                      key={rule.id}
                      onClick={() => handleViewDetails(rule)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleViewDetails(rule);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      className="cursor-pointer transition-colors hover:bg-gray-800/50 focus:bg-gray-800/50 focus:outline-none"
                      aria-label={name}
                    >
                      <td className="overflow-hidden px-4 py-4 sm:px-6">
                        <div
                          className="truncate text-sm font-semibold text-white sm:text-base"
                          title={name}
                        >
                          {name}
                        </div>
                      </td>
                      <td className="overflow-hidden px-4 py-4 sm:px-6">
                        <div
                          className="truncate text-sm text-gray-400"
                          title={rule.description || ""}
                        >
                          {description || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-4 sm:px-6">
                        <span className="text-sm text-gray-300">
                          {formatDelay(rule)}
                        </span>
                      </td>
                      <td
                        className="px-4 py-4 text-center sm:px-6"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <div className="flex flex-col items-center justify-center gap-1.5 sm:flex-row sm:gap-2">
                          <Switch
                            checked={rule.enabled !== false}
                            disabled={togglingRuleId === rule.id}
                            onCheckedChange={(checked) =>
                              handleRuleEnabledChange(rule, checked)
                            }
                            aria-label={t("toggle.aria")}
                          />
                          <span
                            className={`text-xs font-medium whitespace-nowrap ${
                              rule.enabled ? "text-green-400" : "text-gray-500"
                            }`}
                          >
                            {rule.enabled
                              ? t("status.enabled")
                              : t("status.disabled")}
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 ? (
            <div className="sticky bottom-0 z-10 mt-4 flex flex-col items-center justify-between gap-4 rounded-xl border border-gray-800 bg-gray-900 p-4 shadow-[0_-4px_12px_rgba(0,0,0,0.3)] sm:flex-row">
              <div className="text-sm text-gray-400">
                {t("pagination.showing", {
                  from: (currentPage - 1) * CAMPAIGNS_PER_PAGE + 1,
                  to: Math.min(
                    currentPage * CAMPAIGNS_PER_PAGE,
                    pagination?.total || 0,
                  ),
                  total: pagination?.total || 0,
                })}
              </div>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                maxVisiblePages={MAX_VISIBLE_PAGES}
                disabled={isLoading}
              />
            </div>
          ) : null}
        </>
      ) : (
        <div className="rounded-xl border border-gray-800 bg-gray-900 p-12 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-purple-500/20">
            <Icon name="ShoppingCart" className="h-8 w-8 text-purple-400" />
          </div>
          <h3 className="mb-2 text-lg font-medium text-white">
            {t("empty.title")}
          </h3>
          <p className="mb-6 text-gray-400">
            {searchTerm
              ? t("empty.subtitleSearch")
              : t("empty.subtitleDefault")}
          </p>
          <button
            type="button"
            onClick={handleCreateRule}
            className="inline-flex items-center space-x-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
          >
            <Icon name="Plus" className="h-4 w-4" />
            <span>{t("actions.createRule")}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CartRecoveryPage;
